import { MessageScrollerProvider } from '@neo/primitives/message-scroller'
import { useEffect } from 'react'
import { useFlowId } from '../../../hooks/use-flow-id'
import { registerVibeAgentApi } from '../api-registry'
import { useVibeAgentStore } from '../store'
import { useVibeAgent } from '../use-vibe-agent'
import { VibeApprovalButtons } from './approval-buttons'
import { VibeChatHeader } from './chat-header'
import { VibeInput } from './input'
import { VibeProgressWidget } from './progress-widget'
import { VibePromptSuggestions } from './prompt-suggestions'
import { VibeTranscriptList } from './transcript-list'
import { VibeAgentApiProvider } from './use-vibe-agent-api'

/**
 * Chat root: owns the ONE useVibeAgent instance, shares it via context to the
 * sidebar subtree and via the module registry to out-of-subtree callers, and
 * composes the header / transcript / gates / input. All state lives in the
 * vibe store; children read it themselves.
 */
export const VibeAgentChat = () => {
  const api = useVibeAgent()
  const { isNew } = useFlowId()
  const hasTranscript = useVibeAgentStore((state) => state.transcript.length > 0)

  // Expose the single instance to callers outside the sidebar subtree
  // (title-bar "Test with Vibe", flow-page prompt hand-off) — see api-registry.ts.
  useEffect(() => registerVibeAgentApi(api), [api])

  return (
    <VibeAgentApiProvider value={api}>
      {/* Provider lives at the chat root (context only, renders nothing) so the
          composer can reach the scroller's imperative API — a composer send
          must always bring the sent message into view. Classic bottom-follow:
          no turn anchoring (anchors pin new turns to the viewport TOP, which
          reads as "scrolled away" mid-conversation), open at the end, follow
          the live edge, release on scroll-up. */}
      <MessageScrollerProvider autoScroll defaultScrollPosition="end">
        <div className="flex flex-col h-full w-full p-2">
          <VibeChatHeader />
          <VibeTranscriptList />

          <div className="flex-shrink-0 bg-background/50 backdrop-blur-sm">
            <VibeApprovalButtons />
            <VibeProgressWidget />
            {!hasTranscript && <VibePromptSuggestions variant={isNew ? 'new' : 'existing'} />}
            <div className="pt-2">
              <VibeInput />
            </div>
          </div>
        </div>
      </MessageScrollerProvider>
    </VibeAgentApiProvider>
  )
}
